import { Suspense, useState, useEffect, useRef, type RefObject } from 'react';
import { THEME_COMPONENTS } from '@/themes/components';
import { FIXTURE_CONTENT } from '@/themes/fixtures';
import type { ThemeMetadata } from '@/types/theme';

type Viewport = 'mobile' | 'tablet' | 'desktop';

const VIEWPORT_WIDTHS: Record<Viewport, number> = {
  mobile: 375,
  tablet: 768,
  desktop: 1280,
};

const PANEL_WIDTH = 900;
const PREVIEW_HEIGHT = 600;

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function useFocusTrap(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || nodes.length === 0) {
        if (e.key === 'Tab') e.preventDefault();
        return;
      }
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first)?.focus();
      }
    };
    el.addEventListener('keydown', trap);
    return () => el.removeEventListener('keydown', trap);
  }, [ref]);
}

interface ThemePreviewProps {
  metadata: ThemeMetadata;
  onClose: () => void;
}

export default function ThemePreview({ metadata, onClose }: ThemePreviewProps) {
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const dialogRef = useRef<HTMLDivElement>(null);
  const ThemeComponent = THEME_COMPONENTS[metadata.id];
  const targetWidth = VIEWPORT_WIDTHS[viewport];
  const scale = Math.min(1, PANEL_WIDTH / targetWidth);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useFocusTrap(dialogRef);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${metadata.displayName}`}
        ref={dialogRef}
        tabIndex={-1}
        className="bg-theme-surface rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col focus:outline-none"
      >
        <div className="flex items-center justify-between gap-4 p-4 border-b border-theme-border flex-shrink-0">
          <div className="min-w-0">
            <h2 className="font-semibold text-theme-text-primary">{metadata.displayName}</h2>
            <p className="text-xs text-theme-text-secondary truncate">{metadata.description}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(['mobile', 'tablet', 'desktop'] as Viewport[]).map((v) => (
              <button
                key={v}
                onClick={() => setViewport(v)}
                className={`px-3 py-1 rounded text-sm capitalize transition-colors ${
                  viewport === v
                    ? 'bg-theme-accent text-theme-accent-fg'
                    : 'bg-theme-bg text-theme-text-secondary hover:text-theme-text-primary'
                }`}
              >
                {v}
              </button>
            ))}
            <button
              onClick={onClose}
              aria-label="Close preview"
              className="ml-2 p-1.5 rounded text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-bg transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 flex justify-center bg-theme-bg/50">
          {/* Outer container sized to the scaled visual dimensions */}
          <div
            className="relative overflow-hidden flex-shrink-0"
            style={{
              width: targetWidth * scale,
              height: PREVIEW_HEIGHT * scale,
            }}
          >
            <div
              style={{
                width: targetWidth,
                height: PREVIEW_HEIGHT,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                pointerEvents: 'none',
              }}
            >
              <Suspense
                fallback={
                  <div className="h-96 flex items-center justify-center text-theme-text-secondary">
                    Loading…
                  </div>
                }
              >
                {ThemeComponent ? (
                  <ThemeComponent {...FIXTURE_CONTENT} />
                ) : (
                  <div className="h-96 flex items-center justify-center text-theme-text-secondary">
                    Theme not found
                  </div>
                )}
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
